import { Tracker } from '../../analytics/services/Tracker';
import { COMPASS } from '../../analytics/constants/MetricEvents';

/**
 * Typed facade over Tracker.track for the Compass domain.
 *
 * Why: 36 declared events × 157 source files. Centralising the call
 * surface keeps event names and property shapes consistent, and lets
 * us swap `Tracker.track` for batched/async transport later without
 * touching every call site.
 *
 * Pattern: one named function per `metric.compass.*` event. Each
 * function spreads its props into Tracker so payloads stay open for
 * extension without breaking callers.
 *
 * Wiring: import { CompassTracking } and call the method matching the
 * event. Reviewer-ready instrumentation points are listed in
 * docs/domains/compass.md (`Instrumentation` section).
 *
 * Wired call sites (May 2026):
 *   - CompassExplorerControls.tsx — explorerOpened, deepLinkOpened,
 *     lensSwitched, focusDrilled, reviewViewOpened
 *   - CompassSearchPalette.tsx — searchRun (250ms debounce)
 *   - CompassFolderDashboard.tsx — federationGranted,
 *     federationApproved, federatedViewLoaded
 *   - CompassFlowOverlaysSection.tsx — flowOverlayAuthored
 *   - CompassExportSection.tsx — briefingExported
 *   - ComplianceAuditTab.tsx — auditRunFinalized, auditBundleExported
 *   - AuditRunDriftPanel.tsx — auditRunDriftDetected
 *   - ai/tools/complianceEvaluateTool.ts — complianceReportRun, gapsFound,
 *     wellArchitectedReviewCompleted
 *   - import-review/hooks/useIngestInfraScan.ts — sourceConnected, scanCompleted
 *   - import-review/hooks/useCompassChangeSetMutations.ts — changeSet{Accepted,Rejected,Deferred}
 *   - commands/explorer/*LifecycleCommands.ts — findingResolved,
 *     reviewItemStatusChanged, baselineAccepted
 *   - WellArchitectedPillarLens.tsx — wellArchitectedReviewCompleted
 *   - useNetworkReviewWorkflow.ts — reviewItemResolved
 *   - CompassDriftInsight.tsx — driftDetected
 *
 * Pending wire-up (events declared but no UI/server caller exists yet):
 *   - spotlightApplied, gapResolved, evidenceAttached,
 *     coverageThresholdReached, costWasteIdentified, ownershipGapsIdentified,
 *     scenarioReviewed/Approved/Applied (commands exist, no UI caller),
 *     flowOverlayShared, processRepositoryBrowsed,
 *     processRepositoryImpactViewed
 *   These need either a server-side analytics transport (compute-derived
 *   outcomes) or new UI features that fire them.
 */
export const CompassTracking = {
  // ──────────────────────────────────────────────────────────────────
  // Explorer surface
  // ──────────────────────────────────────────────────────────────────
  explorerOpened(props: { folderId: string; source?: 'route' | 'dashboard' | 'deepLink' }): void {
    Tracker.track(COMPASS.EXPLORER_OPENED, props);
  },
  reviewViewOpened(props: { folderId: string; viewId: string }): void {
    Tracker.track(COMPASS.REVIEW_VIEW_OPENED, props);
  },
  lensSwitched(props: { folderId: string; from: string | null; to: string }): void {
    Tracker.track(COMPASS.LENS_SWITCHED, props);
  },
  focusDrilled(props: { folderId: string; fromFocusKey: string | null; toFocusKey: string; zoomLevel: string }): void {
    Tracker.track(COMPASS.FOCUS_DRILLED, props);
  },
  spotlightApplied(props: { folderId: string; predicateId: string; matchedCount: number }): void {
    Tracker.track(COMPASS.SPOTLIGHT_APPLIED, props);
  },
  searchRun(props: { folderId: string; queryLength: number; resultCount: number }): void {
    Tracker.track(COMPASS.SEARCH_RUN, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Compliance + evidence
  // ──────────────────────────────────────────────────────────────────
  complianceReportRun(props: { folderId: string; framework: string; clausesChecked: number }): void {
    Tracker.track(COMPASS.COMPLIANCE_REPORT_RUN, props);
  },
  gapsFound(props: { folderId: string; framework: string; gapCount: number; severityBreakdown?: Record<string, number> }): void {
    Tracker.track(COMPASS.GAPS_FOUND, props);
  },
  gapResolved(props: { folderId: string; gapId: string; framework: string }): void {
    Tracker.track(COMPASS.GAP_RESOLVED, props);
  },
  evidenceAttached(props: { folderId: string; targetType: 'finding' | 'control'; artifactKind: string }): void {
    Tracker.track(COMPASS.EVIDENCE_ATTACHED, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Review state (findings, items, baseline, drift)
  // ──────────────────────────────────────────────────────────────────
  findingResolved(props: { folderId: string; findingId: string; resolution: string }): void {
    Tracker.track(COMPASS.FINDING_RESOLVED, props);
  },
  reviewItemStatusChanged(props: { folderId: string; itemId: string; from: string; to: string }): void {
    Tracker.track(COMPASS.REVIEW_ITEM_STATUS_CHANGED, props);
  },
  reviewItemResolved(props: { folderId: string; itemId: string; resolution: string }): void {
    Tracker.track(COMPASS.REVIEW_ITEM_RESOLVED, props);
  },
  baselineAccepted(props: { folderId: string; baselineId: string; resourceCount: number }): void {
    Tracker.track(COMPASS.BASELINE_ACCEPTED, props);
  },
  driftDetected(props: { folderId: string; baselineId: string; driftedResourceCount: number }): void {
    Tracker.track(COMPASS.DRIFT_DETECTED, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Audit run + bundle export
  // ──────────────────────────────────────────────────────────────────
  auditRunStarted(props: {
    folderId: string;
    framework: string;
    sourceCount?: number;
    clauseCount?: number;
    hasAuditPeriod: boolean;
  }): void {
    Tracker.track(COMPASS.AUDIT_RUN_STARTED, props);
  },
  auditRunCompleted(props: {
    folderId: string;
    runId: string;
    framework: string;
    status: string;
    candidateCount?: number;
    findingCount?: number;
    blockingSourceIssues?: number;
    warningSourceIssues?: number;
  }): void {
    Tracker.track(COMPASS.AUDIT_RUN_COMPLETED, props);
  },
  auditRunFailed(props: { folderId: string; framework: string; reason: string }): void {
    Tracker.track(COMPASS.AUDIT_RUN_FAILED, props);
  },
  auditRunFinalized(props: { folderId: string; runId: string; framework: string }): void {
    Tracker.track(COMPASS.AUDIT_RUN_FINALIZED, props);
  },
  auditBundleExported(props: { folderId: string; bundleId: string; artifactCount: number; sizeBytes?: number }): void {
    Tracker.track(COMPASS.AUDIT_BUNDLE_EXPORTED, props);
  },
  auditExportGenerated(props: {
    folderId: string;
    runId: string;
    format: 'json' | 'csv';
    sizeBytes?: number;
  }): void {
    Tracker.track(COMPASS.AUDIT_EXPORT_GENERATED, props);
  },
  auditReviewerDecisionSaved(props: {
    folderId: string;
    runId: string;
    status?: string;
    decisionCount: number;
  }): void {
    Tracker.track(COMPASS.AUDIT_REVIEWER_DECISION_SAVED, props);
  },
  auditSourceHealthWarning(props: {
    folderId: string;
    runId: string;
    severity: 'warning' | 'blocking';
    warnings: number;
    blocking: number;
    canDrawCleanConclusions: boolean;
  }): void {
    Tracker.track(COMPASS.AUDIT_SOURCE_HEALTH_WARNING, props);
  },
  auditPermissionDenial(props: {
    folderId?: string;
    runId?: string;
    action: 'start_run' | 'finalize_run' | 'export_pack' | 'review_decision';
    status: number;
  }): void {
    Tracker.track(COMPASS.AUDIT_PERMISSION_DENIAL, props);
  },
  auditRunDriftDetected(props: {
    folderId: string;
    framework: string;
    currentRunId: string;
    baseRunId: string;
    introducedInfraFindingCount: number;
    resolvedInfraFindingCount: number;
    clauseImpactChangeCount: number;
  }): void {
    Tracker.track(COMPASS.AUDIT_RUN_DRIFT_DETECTED, props);
  },
  wellArchitectedReviewCompleted(props: {
    folderId: string;
    graphId: string;
    evaluatedAt: string;
    hriCount: number;
    mriCount: number;
    unknownCount: number;
  }): void {
    Tracker.track(COMPASS.WELL_ARCHITECTED_REVIEW_COMPLETED, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Federation
  // ──────────────────────────────────────────────────────────────────
  federationGranted(props: { folderId: string; granteeOrgId: string }): void {
    Tracker.track(COMPASS.FEDERATION_GRANTED, props);
  },
  federationApproved(props: { folderId: string; granterOrgId: string }): void {
    Tracker.track(COMPASS.FEDERATION_APPROVED, props);
  },
  federatedViewLoaded(props: { folderId: string; sourceCount: number; nodeCount: number; edgeCount: number }): void {
    Tracker.track(COMPASS.FEDERATED_VIEW_LOADED, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Empty-state onboarding (GRC framework picker, Compass link tiles, demo)
  // ──────────────────────────────────────────────────────────────────
  /**
   * Fires when a user seeds a bundled compliance framework into a GRC
   * Catalog folder via the framework starter tiles. `source` is `bundled`
   * for in-app catalogs; future OSCAL-uploaded and Vanta/Drata-synced
   * flows can pass `oscal` or `vendor` through the same event for
   * apples-to-apples adoption comparison.
   */
  frameworkSeeded(props: { folderId: string; frameworkId: string; source: 'bundled' | 'oscal' | 'vendor' }): void {
    Tracker.track(COMPASS.FRAMEWORK_SEEDED, props);
  },
  /**
   * Fires when a user clicks one of the empty-state "Link a …" tiles
   * (or "Link a folder") on a Compass folder. `kind` identifies the
   * source folder kind they wanted to link; `null` means the generic
   * un-filtered picker.
   */
  sourceLinkRequested(props: { folderId: string; kind: 'grc-control' | 'network' | 'process' | 'orgchart' | 'software-design' | null }): void {
    Tracker.track(COMPASS.SOURCE_LINK_REQUESTED, props);
  },
  /**
   * Fires once per user when they kick off the Compass demo flow
   * ("Try with sample data") — auto-creates a sibling GRC Catalog
   * folder, seeds SOC 2, and links it back to the current compass folder.
   */
  demoStarted(props: { folderId: string; templateId: 'soc2-and-link' }): void {
    Tracker.track(COMPASS.DEMO_STARTED, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Sources, ingestion, coverage
  // ──────────────────────────────────────────────────────────────────
  sourceConnected(props: { folderId: string; sourceKind: string }): void {
    Tracker.track(COMPASS.SOURCE_CONNECTED, props);
  },
  scanCompleted(props: { folderId: string; sourceKind: string; resourceCount: number; durationMs?: number }): void {
    Tracker.track(COMPASS.SCAN_COMPLETED, props);
  },
  coverageThresholdReached(props: { folderId: string; pct: number; previousPct?: number }): void {
    Tracker.track(COMPASS.COVERAGE_THRESHOLD_REACHED, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Cost / ownership / briefing
  // ──────────────────────────────────────────────────────────────────
  costWasteIdentified(props: { folderId: string; wasteCategory: 'zombie' | 'oversize' | 'untagged'; resourceCount: number; estimatedMonthlySpendUsd?: number }): void {
    Tracker.track(COMPASS.COST_WASTE_IDENTIFIED, props);
  },
  ownershipGapsIdentified(props: { folderId: string; resourceCount: number }): void {
    Tracker.track(COMPASS.OWNERSHIP_GAPS_IDENTIFIED, props);
  },
  briefingExported(props: { folderId: string; format: 'pdf' | 'markdown' | 'html'; sections: number }): void {
    Tracker.track(COMPASS.BRIEFING_EXPORTED, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Scenarios (plan-review)
  // ──────────────────────────────────────────────────────────────────
  scenarioReviewed(props: { folderId: string; scenarioId: string; changeCount: number }): void {
    Tracker.track(COMPASS.SCENARIO_REVIEWED, props);
  },
  scenarioApproved(props: { folderId: string; scenarioId: string }): void {
    Tracker.track(COMPASS.SCENARIO_APPROVED, props);
  },
  scenarioApplied(props: { folderId: string; scenarioId: string; changeSetId: string }): void {
    Tracker.track(COMPASS.SCENARIO_APPLIED, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Flow overlays + deep links
  // ──────────────────────────────────────────────────────────────────
  flowOverlayAuthored(props: { folderId: string; overlayId: string; nodeCount: number }): void {
    Tracker.track(COMPASS.FLOW_OVERLAY_AUTHORED, props);
  },
  flowOverlayShared(props: { folderId: string; overlayId: string; visibility: 'private' | 'team' | 'org' }): void {
    Tracker.track(COMPASS.FLOW_OVERLAY_SHARED, props);
  },
  deepLinkOpened(props: { folderId: string; stateKeys: string[] }): void {
    Tracker.track(COMPASS.DEEP_LINK_OPENED, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Knowledge change sets (Phase E review)
  // ──────────────────────────────────────────────────────────────────
  changeSetAccepted(props: { folderId: string; changeSetId: string; changeCount: number }): void {
    Tracker.track(COMPASS.CHANGE_SET_ACCEPTED, props);
  },
  changeSetRejected(props: { folderId: string; changeSetId: string; reason?: string }): void {
    Tracker.track(COMPASS.CHANGE_SET_REJECTED, props);
  },
  changeSetDeferred(props: { folderId: string; changeSetId: string }): void {
    Tracker.track(COMPASS.CHANGE_SET_DEFERRED, props);
  },

  // ──────────────────────────────────────────────────────────────────
  // Process repository viewpoint
  // ──────────────────────────────────────────────────────────────────
  processRepositoryBrowsed(props: { folderId: string; processCount: number }): void {
    Tracker.track(COMPASS.PROCESS_REPOSITORY_BROWSED, props);
  },
  processRepositoryImpactViewed(props: { folderId: string; processId: string; dependencyCount: number }): void {
    Tracker.track(COMPASS.PROCESS_REPOSITORY_IMPACT_VIEWED, props);
  },
} as const;
